import { useState } from 'react';
import { Building2, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card } from '@/components/ui/card';
import { Eyebrow } from '@/components/ui/eyebrow';
import { Button } from '@/components/ui/button';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { EntidadeDrawer } from './EntidadeDrawer';
import { useEntidades, useDeleteEntidade } from '@/lib/hooks/entidades';

interface Props {
  workspaceId: string;
}

export function EntidadesCard({ workspaceId }: Props) {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; nome: string } | null>(null);

  const { data, isLoading } = useEntidades(workspaceId);
  const deleteMut = useDeleteEntidade(workspaceId);

  const entidades = data?.items ?? [];

  async function handleDelete() {
    if (!deleteTarget) return;
    try {
      await deleteMut.mutateAsync(deleteTarget.id);
      toast.success('Entidade removida.');
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Falha ao remover entidade.');
    } finally {
      setDeleteTarget(null);
    }
  }

  return (
    <>
      <Card variant="default" className="p-5">
        <div className="flex items-start justify-between gap-3 mb-4">
          <div className="flex items-start gap-3">
            <div className="grid h-10 w-10 place-items-center rounded-2 bg-mint-soft text-mint-deep shrink-0">
              <Building2 className="h-5 w-5" strokeWidth={1.8} />
            </div>
            <div>
              <Eyebrow>Cadastro</Eyebrow>
              <h3 className="font-display text-[16px] font-semibold tracking-[-0.015em] text-fg mt-0.5">
                Entidades
              </h3>
              <p className="text-sm text-fg-muted mt-0.5">
                Organizações parceiras vinculadas aos convênios.
              </p>
            </div>
          </div>
          <Button
            variant="primary"
            size="sm"
            onClick={() => setDrawerOpen(true)}
            className="shrink-0"
          >
            <Plus className="h-4 w-4" strokeWidth={1.8} />
            Adicionar
          </Button>
        </div>

        {isLoading ? (
          <div className="space-y-2">
            {[1, 2].map((i) => (
              <div
                key={i}
                className="h-14 rounded-2 bg-card-soft animate-pulse"
              />
            ))}
          </div>
        ) : entidades.length === 0 ? (
          <div className="rounded-2 border border-dashed border-card-stroke bg-card-soft p-6 text-center">
            <p className="text-sm text-fg-muted">
              Nenhuma entidade cadastrada ainda.
            </p>
            <Button
              variant="ghost"
              size="sm"
              className="mt-2"
              onClick={() => setDrawerOpen(true)}
            >
              Cadastrar primeira entidade
            </Button>
          </div>
        ) : (
          <ul className="divide-y divide-card-stroke">
            {entidades.map((e) => (
              <li
                key={e.id}
                className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-sm font-medium text-fg truncate">
                    {e.nomeFantasia ?? e.razaoSocial}
                  </p>
                  <p className="text-xs text-fg-muted font-mono mt-0.5">
                    {formatCnpj(e.cnpj)}
                    {e.municipioUf && (
                      <span className="ml-2 not-italic font-sans">{e.municipioUf}</span>
                    )}
                  </p>
                </div>
                <button
                  type="button"
                  aria-label={`Remover entidade ${e.nomeFantasia ?? e.razaoSocial}`}
                  className="grid h-8 w-8 place-items-center rounded-2 text-fg-dim hover:text-warn hover:bg-warn-soft transition-colors shrink-0"
                  onClick={() =>
                    setDeleteTarget({ id: e.id, nome: e.nomeFantasia ?? e.razaoSocial })
                  }
                >
                  <Trash2 className="h-4 w-4" strokeWidth={1.8} />
                </button>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <EntidadeDrawer
        workspaceId={workspaceId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        onOpenChange={(v) => { if (!v) setDeleteTarget(null); }}
        title="Remover entidade?"
        description={`"${deleteTarget?.nome}" será removida permanentemente. Esta ação não pode ser desfeita.`}
        confirmLabel="Remover"
        tone="danger"
        loading={deleteMut.isPending}
        onConfirm={handleDelete}
      />
    </>
  );
}

function formatCnpj(cnpj: string): string {
  const d = cnpj.replace(/\D/g, '');
  if (d.length !== 14) return cnpj;
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
}
